import { Component, ComponentRef, computed, EventEmitter, Input, input, InputSignal, Output, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DsBreakpoints, DsButtonVariants, Icons } from '@levelaccess/design-system';

import { FileExtension } from '../../../../shared/constants/file-extension';
import { ModalService } from '../../services/modal.service';
import {
  ShareReportModalComponent,
  ShareReportModalInputData,
} from '../../pages/public/scans/scan-results/view-scan-results-summary/share-report-modal/share-report-modal.component';
import { IModalDialogInputData } from '../../interfaces/modal-dialog.interface';
import { EapColors } from '../../shared/eap-colors';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { WindowService } from '../../services/window.service';

// We now have a docx report but it doesn't require an icon and there isn't a docx icon in the design system so I made this a partial
const FileTypeIcons: Partial<Record<FileExtension, Icons>> = {
  [FileExtension.csv]: Icons.FileInvoice,
  [FileExtension.xlsx]: Icons.FileExcel,
};

@Component({
  selector: 'app-export-report-button',
  templateUrl: './export-report-button.component.html',
  styleUrls: ['./export-report-button.component.scss'],
  standalone: false,
})
export class ExportReportButtonComponent {
  @Output() public onDownloadReport: EventEmitter<FileExtension>;
  @Input() public fileExtensions: FileExtension[];
  @Input() public shareModalInputData: IModalDialogInputData<ShareReportModalInputData>;
  @Input() public dropdownButtonLabel: string = 'action_export';
  @Input() public dropdownButtonIcon: Icons = Icons.ArrowDownToBracket;
  @Input() public canDownloadReport: boolean = true;
  public readonly responsiveVariant: InputSignal<boolean> = input(false);

  public Icons: typeof Icons;
  public EapColors: typeof EapColors;
  public DsButtonVariants: typeof DsButtonVariants;
  public FileTypeIcons: typeof FileTypeIcons;

  public readonly isSmallViewport: Signal<boolean>;
  public readonly displaySecondaryToggle: Signal<boolean>;

  constructor(
    private modalService: ModalService,
    private windowService: WindowService,
  ) {
    this.Icons = Icons;
    this.EapColors = EapColors;
    this.DsButtonVariants = DsButtonVariants;
    this.onDownloadReport = new EventEmitter<FileExtension>();
    this.FileTypeIcons = FileTypeIcons;

    this.isSmallViewport = toSignal(this.windowService.observeBreakpoint(DsBreakpoints.Small), { initialValue: false });
    this.displaySecondaryToggle = computed((): boolean => this.responsiveVariant() && this.isSmallViewport());
  }

  public get isShareable(): boolean {
    return SharedCommonUtility.notNullish(this.shareModalInputData);
  }

  public onDownloadReportRequest(fileExtension: FileExtension): void {
    this.onDownloadReport.emit(fileExtension);
  }

  public onShareReportRequest(): void {
    const modalComponentRef: ComponentRef<ShareReportModalComponent> = this.modalService.open(ShareReportModalComponent);
    modalComponentRef.instance.modalInputData = this.shareModalInputData;
  }
}
